import { useEffect, useRef } from "preact/hooks";
import { AltHintTarget } from "../AltHintTarget/AltHintTarget.jsx";
import { IconArrowKeyDown, IconArrowKeyUp } from "../Icon/Icon.jsx";
import { Panel } from "../Panel/Panel.jsx";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import "./SideMenu.css";

export function SideMenu({
  items,
  value,
  onChange,
  showAltHints = false,
  hintLabel = "S",
  focusBinding = null,
  footer = null
}) {
  const itemRefs = useRef([]);
  const activeIndex = Math.max(0, items.findIndex((item) => item.value === value));
  const previousItemIndex = activeIndex > 0 ? activeIndex - 1 : -1;
  const nextItemIndex = activeIndex >= 0 && activeIndex < items.length - 1 ? activeIndex + 1 : -1;

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
  }, [items.length]);

  function focusItemByIndex(index) {
    const boundedIndex = Math.max(0, Math.min(items.length - 1, index));
    itemRefs.current[boundedIndex]?.focus({ preventScroll: true });
  }

  function focusActiveItem() {
    focusItemByIndex(activeIndex);
  }

  const globalBindings = !focusBinding
    ? []
    : [
      onKey(focusBinding, () => {
        focusActiveItem();
      }, { allowInInput: true })
    ];

  useShortcutBindings(window, globalBindings);

  return (
    <AltHintTarget label={hintLabel} visible={showAltHints} fill>
      <Panel scroll className="fill-area" footer={footer}>
        <nav class="side-menu">
          {items.map((item, index) => {
            const button = (
              <button
                key={item.value}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                class={`side-menu-item${item.value === value ? " active" : ""}`}
                type="button"
                onClick={() => onChange(item.value)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowUp") {
                    event.preventDefault();
                    focusItemByIndex(index - 1);
                    return;
                  }

                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    focusItemByIndex(index + 1);
                  }
                }}
              >
                <span class="side-menu-item-label">{item.label}</span>
                {item.description ? (
                  <span class="side-menu-item-description">{item.description}</span>
                ) : null}
              </button>
            );

            if (index !== nextItemIndex && index !== previousItemIndex) {
              return button;
            }

            return (
              <AltHintTarget
                key={`${item.value}-hint`}
                visible={showAltHints}
                label={index === previousItemIndex
                  ? <IconArrowKeyUp className="alt-hint-badge-icon" />
                  : <IconArrowKeyDown className="alt-hint-badge-icon" />}
              >
                {button}
              </AltHintTarget>
            );
          })}
        </nav>
      </Panel>
    </AltHintTarget>
  );
}
